import type { BlockProps } from "./blocks/registry";
import HomeHero from "../design-system/organisms/HomeHero";

import { Heading } from "../design-system/atoms/Heading";
import { Paragraph } from "../design-system/atoms/Paragraph";
import Image from "next/image";
import Link from "next/link";
import { resolveLegacyLink } from "../utils/linkUtils";
import { useEffect, useState } from "react";
import { buildProspectLink } from "../utils/prospectLink";
import PreCommandePopup from "./CommandePopup";
import { usePathname } from "next/navigation";

type StrapiMedia = {
  url?: string;
  alternativeText?: string;
};

type CtaItem = {
  id?: number | string;
  text: string;
  link: string;
};

type HeroFeature = {
  id?: number | string;
  titre?: string;
  descr?: string;
  cta?: CtaItem;
  icon?: StrapiMedia;
  iconHover?: StrapiMedia;
};

type HeroBlockData = {
  id?: number | string;
  documentId?: string;
  __component?: string;
  primary_Title?: string;
  secondary_Title?: string;
  hero_image?: StrapiMedia;
  variant?: "home" | "prescripteur" | string;
  cta?: CtaItem[];
  features?: HeroFeature[];
};

type MappedHeroFeature = {
  id: string;
  title: string;
  description: string;
  href: string;
  iconSrc: string;
  iconHoverSrc: string;
};

type MappedHeroData = {
  primaryTitle: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  features: MappedHeroFeature[];
};

export function mapHeroData(strapiData: HeroBlockData): MappedHeroData {
  return {
    primaryTitle: strapiData.primary_Title || "",
    description: strapiData.secondary_Title || "",
    imageSrc: strapiData.hero_image?.url || "/fallback-image.jpg",
    imageAlt: strapiData.hero_image?.alternativeText || "Image Hero",

    features: (strapiData.features || []).map((f, index) => ({
      id: String(f.id ?? `feature-${index}`),
      title: f.titre || "",
      description: f.descr || "",
      href: f.cta?.link || "#",
      iconSrc: f.icon?.url || "/icons/default.svg",
      iconHoverSrc: f.iconHover?.url || "/icons/default-hover.svg",
    })),
  };
}

export default function Hero({ data }: BlockProps<HeroBlockData>) {
  const [commanderHref, setCommanderHref] = useState<string>(
    resolveLegacyLink("/parcours-prospects", "clients")
  );

  useEffect(() => {
    const link =
      buildProspectLink() || resolveLegacyLink("/parcours-prospects", "clients");

    setCommanderHref(link);
  }, []);

  const [showPreCommandePopup, setShowPreCommandePopup] = useState(false);

  if (!data) return null;

  const { primary_Title, secondary_Title, hero_image, cta, variant } = data;

  const heroData = mapHeroData(data);

  const pathname = usePathname();

  const pathParts = pathname.split("/").filter(Boolean);

  const agencySlug =
    pathParts[0] === "portage-de-repas"
      ? pathParts[1] || ""
      : "";

  const resolveAgencyLink = (link?: string) => {
    if (!link) return "#";

    // Lien externe
    if (/^https?:\/\//i.test(link)) {
      return link;
    }

    if (!agencySlug) {
      return link;
    }

    // Accueil de l'agence
    if (link === "/") {
      return `/portage-de-repas/${agencySlug}`;
    }

    const normalizedLink = link.startsWith("/")
      ? link
      : `/${link}`;

    return `/portage-de-repas/${agencySlug}${normalizedLink}`;
  };

  if (variant === "home") {
    return <HomeHero data={heroData} key={data.documentId} />;
  }
  if (variant === "prescripteur") {
    const imageUrl = data.hero_image?.url || "/fallback-image.jpg";
    const imageAlt = data.hero_image?.alternativeText || "";
    return (
      <section className="min-h-[80vh] relative">
        {/* Colonne gauche - Texte + dégradé */}
        <div className="lms-container grid lg:grid-cols-2 grid-cols-1">
          <Image
            src={imageUrl}
            alt={imageAlt}
            priority
            fill={true}
            style={{
              objectFit: "cover",
            }}
          />
          <div className="h-[800px] flex flex-col justify-center  relative">
            <Heading
              as="h1"
              className="text-4xl lg:text-[44px] font-bold leading-tight mb-6 text-white"
            >
              {primary_Title}
            </Heading>

            <Paragraph className="text-white">
              {secondary_Title}
            </Paragraph>

            <div className="flex flex-wrap gap-3">
              {cta?.map((btn, i) => (
                <Link href={btn.link} key={i}>
                  <button
                    key={i}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${i === 0
                      ? "bg-secondary text-white "
                      : "bg-white text-primaryGreen border border-gray-300 hover:bg-primaryGreen hover:text-white"
                      }`}
                  >
                    {btn.text}
                  </button>
                </Link>

              ))}
            </div>
          </div>
        </div>

      </section>
    );
  }
  if (variant === "agence") {
    const imageUrl = data.hero_image?.url || "";
    const features = data.features || [];

    return (
      <>  <section className="relative py-6 lg:py-0">
        <div className="lms-container relative">

          {/* HERO */}
          <div
           className="relative min-h-[430px] overflow-hidden rounded-[28px] bg-cover bg-[position:57%_center] bg-no-repeat sm:bg-[position:60%_center] lg:min-h-[450px] lg:bg-[position:65%_center] xl:min-h-[500px] xl:bg-[position:100%_center] 2xl:min-h-[580px]"
            style={{
              backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
            }}
          >

            {/* CONTENU */}
            <div className="absolute inset-0 z-10 flex items-center px-7 sm:px-10 lg:px-14">
              <div className="max-w-[540px]">
                <Heading
                  as="h1"
                  className="mb-5 text-4xl lg:text-[44px] leading-tight text-primaryGreen "
                >
                  {primary_Title}
                </Heading>

                {secondary_Title && (
                  <Paragraph className="max-w-[520px] text-lg text-textColor lg:text-xl">
                    {secondary_Title}
                  </Paragraph>
                )}

                {cta && cta.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {cta.map((btn: any, i: number) => {
                      const isCommander =
                        btn.text?.trim().toLowerCase() === "commander";

                      const buttonClassName = `rounded-lg px-4 py-3 font-semibold uppercase transition-all duration-300 ${i === 0
                        ? "border border-gray-300 bg-white text-textColor hover:border-primaryGreen hover:text-primaryGreen"
                        : "bg-secondary text-white hover:opacity-90"
                        }`;

                      if (isCommander) {
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setShowPreCommandePopup(true)}
                            className={buttonClassName}
                          >
                            {btn.text}
                          </button>
                        );
                      }

                      return (
                        <Link
                          key={i}
                          href={resolveAgencyLink(btn.link)}
                        >
                          <button
                            type="button"
                            className={buttonClassName}
                          >
                            {btn.text}
                          </button>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* BANDEAU DE RÉASSURANCE */}
          {features.length > 0 && (
            <div className="relative z-20 mx-0 mt-4 rounded-[24px] border-[6px] border-white bg-[linear-gradient(180deg,#F0FFC5_61.37%,#D8F794_100%)] shadow-xl lg:-mt-12 lg:mx-6">  <div className="grid grid-cols-1 gap-6 px-6 py-7 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
              {features.map((feature: any, i: number) => {
                const iconUrl = feature.icon?.url || "";
                const iconAlt =
                  feature.icon?.alternativeText ||
                  feature.titre ||
                  "";

                return (
                  <div key={i} className="flex items-center gap-4">
                    {iconUrl && (
                      <div className="relative h-14 w-14 shrink-0">
                        <Image
                          src={iconUrl}
                          alt={iconAlt}
                          fill
                          className="object-contain"
                        />
                      </div>
                    )}

                    <div>
                      {feature.titre && (
                        <p className="font-bold text-textColor">
                          {feature.titre}
                        </p>
                      )}

                      {feature.descr && (
                        <p className="mt-1 text-sm text-textColor">
                          {feature.descr}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          )}

        </div>
      </section>
        {/* POPUP COMMANDER */}
        {showPreCommandePopup && (
          <PreCommandePopup
            onClose={() => setShowPreCommandePopup(false)}
            parcoursHref={commanderHref}
          />
        )}
      </>

    );
  }

  if (variant === "page-agence") {
    return (
      <>
        <section
          key={data.id}
          className="relative min-h-[450px] xl:min-h-[520px] 2xl:min-h-[650px]"
        >
          <div className="lms-container grid lg:grid-cols-2 grid-cols-1">
            {/* Image de fond */}
            {hero_image?.url && (
              <Image
                src={hero_image.url}
                alt={hero_image.alternativeText || primary_Title || "Hero image"}
                priority
                fill
                className="object-cover object-top"
              />
            )}

            {/* Dégradé blanc à gauche */}
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(246,246,246,0.88)_0%,rgba(246,246,246,0.75)_45%,rgba(246,246,246,0.45)_70%,rgba(246,246,246,0.18)_100%)] lg:bg-[linear-gradient(90deg,#F6F6F6_0%,#F6F6F6_34%,rgba(246,246,246,0.94)_43%,rgba(246,246,246,0.55)_52%,transparent_66%)]" />
            {/* Contenu */}
            {/* Contenu */}
            <div className="relative flex min-h-[450px] flex-col justify-center xl:min-h-[520px] 2xl:min-h-[650px]">
              <div className="relative flex h-full flex-col justify-center">
                <Heading
                  as="h1"
                  className="mb-6 font-playfair text-4xl lg:text-[44px] font-bold leading-tight text-primaryGreen "
                >
                  {primary_Title}
                </Heading>

                {secondary_Title && (
                  <Paragraph className="max-w-[850px] text-[22px] font-medium leading-snug text-text lg:text-[26px]">
                    {secondary_Title}
                  </Paragraph>
                )}

                {cta && cta.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {cta.map((btn: any, i: number) => {
                      const isCommander =
                        btn.text?.trim().toLowerCase() === "commander";

                      const buttonClassName = `rounded-lg px-5 py-3 font-semibold uppercase transition-all duration-300 ${isCommander
                        ? "bg-secondary text-white hover:opacity-90"
                        : i === 0
                          ? "border border-gray-300 bg-white text-textColor hover:border-primaryGreen hover:text-primaryGreen"
                          : "bg-secondary text-white hover:opacity-90"
                        }`;

                      if (isCommander) {
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setShowPreCommandePopup(true)}
                            className={buttonClassName}
                          >
                            {btn.text}
                          </button>
                        );
                      }

                      return (
                        <Link
                          key={i}
                          href={resolveAgencyLink(btn.link)}
                        >
                          <button
                            type="button"
                            className={buttonClassName}
                          >
                            {btn.text}
                          </button>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>


        </section>
        {/* POPUP COMMANDER */}
        {showPreCommandePopup && (
          <PreCommandePopup
            onClose={() => setShowPreCommandePopup(false)}
            parcoursHref={commanderHref}
          />
        )}
      </>

    );
  }

  if (variant === "ville") {
    return (
      <section
        key={data.id}
        className="relative min-h-[80vh]"
      >
        <div className="lms-container grid lg:grid-cols-2 grid-cols-1">
          {/* Image de fond */}
          {hero_image?.url && (
            <Image
              src={hero_image.url}
              alt={hero_image.alternativeText || primary_Title || "Hero image"}
              priority
              fill={true}
              style={{
                objectFit: "cover",
              }}
            />
          )}

          {/* Dégradé blanc à gauche */}
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#F6F6F6_0%,#F6F6F6_34%,rgba(246,246,246,0.94)_43%,rgba(246,246,246,0.55)_52%,transparent_66%)]" />

          {/* Contenu */}
          <div className="h-[800px]">
            <div className="h-full flex flex-col justify-center relative">
              <Heading
                as="h1"
                className="mb-6 font-playfair text-4xl font-bold leading-tight text-primaryGreen lg:text-[44px] "
              >
                {primary_Title}
              </Heading>

              {secondary_Title && (
                <Paragraph className="max-w-[850px] text-[22px] font-medium leading-snug text-text lg:text-[26px]">
                  {secondary_Title}
                </Paragraph>
              )}

              {cta && cta.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-4">
                  {cta.map((btn: any, i: number) => (
                    <Link href={btn.link || "#"} key={i}>
                      <button
                        type="button"
                        className={`min-w-[210px] rounded-lg px-7 py-4 font-semibold uppercase transition-all duration-300 ${i === 0
                          ? "border border-gray-300 bg-white text-textColor hover:border-primaryGreen hover:text-primaryGreen"
                          : "bg-secondary text-white hover:opacity-90"
                          }`}
                      >
                        {btn.text}
                      </button>
                    </Link>
                  ))}
                </div>
              )}

              <div >
                <Image
                  src="https://site-lms.fra1.digitaloceanspaces.com/Frame_558_3_5c47a6cc40.png"
                  alt="Illustration des villes"
                  width={300}
                  height={200}
                  className="mt-8"
                />
              </div>
            </div>
          </div>
        </div>


      </section>
    )
  }

  else {

    return (
      <section className="relative  lms-container pb-12  overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-10  items-center rounded-3xl bg-muted">
          <div className="max-w-2xl px-7 pt-3">
            <Heading
              as="h1"
              className="text-4xl lg:text-[44px] font-bold "
            >
              {primary_Title}
            </Heading>

            <Paragraph>{secondary_Title}</Paragraph>

            <div className="flex flex-wrap gap-3 pt-3">
              {cta?.map((btn, i) => {
                const isCommander = btn.text?.trim().toUpperCase() === "COMMANDER";

                const buttonClassName = `px-6 py-3 rounded-lg font-semibold transition ${i === 0
                  ? "bg-white text-primaryGreen border border-gray-400 hover:bg-primaryGreen hover:text-white"
                  : "bg-secondary text-white hover:bg-primaryGreen"
                  }`;

                if (isCommander) {
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setShowPreCommandePopup(true)}
                      className={buttonClassName}
                    >
                      {btn.text}
                    </button>
                  );
                }

                return (
                  <Link href={resolveLegacyLink(btn.link)} key={i}>
                    <button type="button" className={buttonClassName}>
                      {btn.text}
                    </button>
                  </Link>
                );
              })}
            </div>

            {showPreCommandePopup && (
              <PreCommandePopup
                onClose={() => setShowPreCommandePopup(false)}
                parcoursHref={commanderHref}
              />
            )}
          </div>
          <div className="flex justify-center lg:justify-end">
            <Image
              src={hero_image?.url || "/fallback-image.jpg"}
              alt={hero_image?.alternativeText || ""}
              width={748}
              height={800}
              className="rounded-3xl"
              priority
            />
          </div>
        </div>
      </section>
    );
  }
}